import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { TrendingUp, Calculator, DollarSign, Target, FileSpreadsheet } from 'lucide-react';
import { getCurrentResultsByTbId } from '../services/uiShim';
import API from '../services/apiBase';

const RatesSummary = ({ calculation, trialBalance }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // shim-adapted { rates, pools, bases }
  const [gaHeadcount, setGaHeadcount] = useState('');
  const [ohHeadcount, setOhHeadcount] = useState('');
  const [savingHeadcount, setSavingHeadcount] = useState(false);

  const tenantId = (trialBalance && trialBalance.tenant_id) || calculation?.tenant_id || '';
  const tbId = calculation?.tb_id || trialBalance?.id;

  useEffect(() => {
    if (tenantId && tbId) {
      loadViaShim();
    }
     
  }, [tenantId, tbId, calculation?.id, calculation?.recalculated_at]);

  const loadViaShim = async () => {
    setLoading(true);
    setError(null);
    try {
      const shim = await getCurrentResultsByTbId(tenantId, tbId);
      setData(shim);
    } catch (e) {
      console.warn('Failed to load rate data via shim:', e);
      setError('Failed to load rate data');
    } finally {
      setLoading(false);
    }
  };

  const saveHeadcount = async () => {
    if (!tbId) return;
    const ga = Number(gaHeadcount);
    const oh = Number(ohHeadcount);
    if (!Number.isFinite(ga) || !Number.isFinite(oh)) {
      alert('Please enter numeric headcount values');
      return;
    }
    setSavingHeadcount(true);
    try {
      // Set headcount
      const res = await fetch(`${API}/trial-balance/${encodeURIComponent(tbId)}/headcount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ga, oh })
      });
      if (!res.ok) throw new Error(await res.text());
      // Recalculate
      const recalcPayload = {};
      if (tenantId) recalcPayload.tenant_id = tenantId;
      const recalc = await fetch(`${API}/calculation/${encodeURIComponent(tbId)}/recalculate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(recalcPayload) });
      if (!recalc.ok) throw new Error(await recalc.text());
      await loadViaShim();
      alert('Headcount saved and rates recalculated');
    } catch (e) {
      console.error('Headcount save error:', e);
      alert('Failed to save headcount');
    } finally {
      setSavingHeadcount(false);
    }
  };

  const formatPercentage = (value) => {
    const num = Number(value || 0);
    const pct = num <= 1 ? num * 100 : num;
    return `${pct.toFixed(2)}%`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  if (!tenantId || !tbId) {
    return (
      <div className="text-center py-12">
        <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600">No calculation data available</p>
        <p className="text-sm text-gray-500">Upload a trial balance file to view rates</p>
      </div>
    );
  }

  const pools = data?.pools || {};
  const bases = data?.bases || {};
  const rates = data?.rates || { ga: '0.00%', oh: '0.00%', fringe: '0.00%' };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rates &amp; Summary</h2>
          <p className="text-gray-600">Overview of calculated indirect cost rates</p>
        </div>
        <div className="flex items-center gap-2">
          {calculation?.filename && (
            <Badge variant="outline" className="bg-green-50 text-green-700">
              {calculation.filename}
            </Badge>
          )}
          <Badge variant="outline">DCAA Compliant</Badge>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Headcount mini-form */}
      <Card>
        <CardHeader>
          <h3 className="text-md font-semibold">Intermediate Allocation Headcount</h3>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-xs text-gray-600 mb-1">G&amp;A Headcount</label>
              <input type="number" className="border rounded px-2 py-1 w-32" value={gaHeadcount} onChange={(e) => setGaHeadcount(e.target.value)} placeholder="e.g. 7" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">OH Headcount</label>
              <input type="number" className="border rounded px-2 py-1 w-32" value={ohHeadcount} onChange={(e) => setOhHeadcount(e.target.value)} placeholder="e.g. 3" />
            </div>
            <Button onClick={saveHeadcount} disabled={savingHeadcount}>
              {savingHeadcount ? 'Saving...' : 'Save & Recalculate'}
            </Button>
          </div>
          <div className="text-xs text-gray-500 mt-2">If an explicit intermediate override is set, it takes precedence over headcount.</div>
        </CardContent>
      </Card>

      {/* Rate Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <RateCard title="G&amp;A Rate" rate={rates.ga} icon={Target} color="blue" description="General &amp; Administrative" pool={pools['G&A']} />
        <RateCard title="Overhead Rate" rate={rates.oh} icon={Calculator} color="green" description="Manufacturing Overhead" pool={pools['OH']} />
        <RateCard title="Fringe Rate" rate={rates.fringe} icon={TrendingUp} color="blue" description="Employee Benefits" pool={pools['Fringe']} />
        <RateCard title="COM Rate" rate={formatPercentage(0)} icon={DollarSign} color="orange" description="Cost of Money" pool={0} />
      </div>

      {/* Rate Calculation Summary */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Rate Calculation Summary
          </h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium mb-3">G&amp;A Calculation</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>G&amp;A Pool:</span><span>{formatCurrency(pools['G&A'] || 0)}</span></div>
                <div className="flex justify-between"><span>G&amp;A Base:</span><span>{formatCurrency(bases['G&A'] || 0)}</span></div>
                <div className="flex justify-between border-t pt-2 font-medium"><span>Rate:</span><span>{rates.ga}</span></div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Overhead Calculation</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>OH Pool:</span><span>{formatCurrency(pools['OH'] || 0)}</span></div>
                <div className="flex justify-between"><span>OH Base:</span><span>{formatCurrency(bases['OH'] || 0)}</span></div>
                <div className="flex justify-between border-t pt-2 font-medium"><span>Rate:</span><span>{rates.oh}</span></div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Fringe Calculation</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Fringe Pool:</span><span>{formatCurrency(pools['Fringe'] || 0)}</span></div>
                <div className="flex justify-between"><span>Fringe Base:</span><span>{formatCurrency(bases['Fringe'] || 0)}</span></div>
                <div className="flex justify-between border-t pt-2 font-medium"><span>Rate:</span><span>{rates.fringe}</span></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const RateCard = ({ title, rate, icon: Icon, color, description, pool }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600 text-white',
    green: 'from-green-500 to-green-600 text-white',
    orange: 'from-orange-500 to-orange-600 text-white'
  };
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
  return (
    <Card className="overflow-hidden">
      <div className={`bg-gradient-to-br ${colorClasses[color]} p-4`}>
        <div className="flex items-center justify-between">
          <Icon className="w-8 h-8 opacity-80" />
          <div className="text-right">
            <div className="text-2xl font-bold">{rate}</div>
            <div className="text-xs opacity-80">{title}</div>
          </div>
        </div>
      </div>
      <CardContent className="p-4">
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">{description}</div>
          {pool !== undefined && (
            <div className="text-xs text-gray-500">Pool: {formatCurrency(pool)}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RatesSummary;
